import { SERVICE } from '@skyscanner/strevda-tracking';
import { ADVERT_FORMAT_TYPES } from '@web-platform/shared-universal-constants/src/adverts';
import { generateAdvertIdFromCreative } from '@web-platform/shared-universal-utils-frontend/src/helpers/generateAdvertId';

import {
  getBrandBannerCreative,
  getCompetitorBrandBannerCreative,
  getFlexibleSearchCreative,
  getFlightsInlineCreative,
  getFlightsPricedBrandInlineCreative,
  getFlightsUnpricedBrandInlineCreative,
  getItineraryInlinePlusCreative,
  getPackagesCreative,
  getPackagesPricedCreative,
} from './creativesFromConductor';
import { getFlightsAlternateDatesInlineCreative } from './creativesFromConductor/getFlightsAlternateDatesInlineCreative';

import type { RadarCreative } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarCreativeTypes';
import type {
  Advert,
  ConductorCreative,
} from '@web-platform/shared-universal-types/src/redux/acorn/state/flightsDayView/entitiesSearchResults/adverts/types';
import type { ID } from '@web-platform/shared-universal-types-utils/src/utils';

const getAdvertProps = (creative: ConductorCreative | RadarCreative) => {
  const id: ID = generateAdvertIdFromCreative(creative);
  const { fields, pixels } = creative;
  const performanceEventsPayloads =
    'performanceEventsPayloads' in creative ? creative.performanceEventsPayloads : undefined;

  const analyticsProperties =
    'analytics_properties' in creative
      ? creative.analytics_properties
      : creative.analyticsProperties;

  const idFormat = 'id_format' in creative ? creative.id_format : creative.idFormat;

  const {
    id_campaign,
    id_creative,
    id_pixel_creative,
    id_placement,
    id_sponsor,
    id_tracking,
    priority,
  } = analyticsProperties;
  const advertProps: Omit<Advert, 'creative'> = {
    id,
    position: parseInt(fields.appIndex, 10),
    analyticsProperties: {
      idPixelCreative: id_pixel_creative,
      idCreative: id_creative,
      idPlacement: id_placement,
      idTracking: id_tracking,
      idSponsor: id_sponsor,
      idCampaign: id_campaign,
      idFormat,
      service: SERVICE.SAM,
      priority,
    },
    pixels,
    performanceEventsPayloads,
  };
  return advertProps;
};

const getConductorCreative = (creative: ConductorCreative) => {
  const { fields, id_format: idFormat } = creative;
  switch (idFormat) {
    case ADVERT_FORMAT_TYPES.ITINERARY_INLINE_PLUS:
      return getItineraryInlinePlusCreative(fields);
    case ADVERT_FORMAT_TYPES.BRAND_BANNER:
      return getBrandBannerCreative(fields);
    case ADVERT_FORMAT_TYPES.COMPETITOR_BRAND_BANNER:
      return getCompetitorBrandBannerCreative(fields);
    case ADVERT_FORMAT_TYPES.INLINE:
      return getFlightsInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE:
      return getFlightsPricedBrandInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE:
      return getFlightsUnpricedBrandInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.ALTERNATE_DATES_INLINE:
      return getFlightsAlternateDatesInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.PACKAGES_INLINE:
      return getPackagesCreative(fields);
    case ADVERT_FORMAT_TYPES.PACKAGES_PRICED:
      return getPackagesPricedCreative(fields);
    case ADVERT_FORMAT_TYPES.FLEXIBLE_SEARCH:
      return getFlexibleSearchCreative(fields);
    default:
      return { ...fields, format: idFormat };
  }
};

// Have to split the transfer function for RadarCreative due to the type inference conflict problem
const getRadarCreative = (creative: RadarCreative) => {
  const { fields, idFormat } = creative;
  switch (idFormat) {
    case ADVERT_FORMAT_TYPES.ITINERARY_INLINE_PLUS:
      return getItineraryInlinePlusCreative(fields);
    case ADVERT_FORMAT_TYPES.BRAND_BANNER:
      return getBrandBannerCreative(fields);
    case ADVERT_FORMAT_TYPES.COMPETITOR_BRAND_BANNER:
      return getCompetitorBrandBannerCreative(fields);
    case ADVERT_FORMAT_TYPES.INLINE:
      return getFlightsInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE:
      return getFlightsPricedBrandInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE:
      return getFlightsUnpricedBrandInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.ALTERNATE_DATES_INLINE:
      return getFlightsAlternateDatesInlineCreative(fields);
    case ADVERT_FORMAT_TYPES.PACKAGES_INLINE:
      return getPackagesCreative(fields);
    case ADVERT_FORMAT_TYPES.PACKAGES_PRICED:
      return getPackagesPricedCreative(fields);
    case ADVERT_FORMAT_TYPES.FLEXIBLE_SEARCH:
      return getFlexibleSearchCreative(fields);
    default:
      return { ...fields, format: idFormat };
  }
};

const isRadarCreative = (
  creative: ConductorCreative | RadarCreative,
  fromRadar: boolean,
): creative is RadarCreative => fromRadar;

export const transformCreativeToAdvert = (
  creative: ConductorCreative | RadarCreative,
  fromRadar: boolean = false,
): Advert => {
  const advertProps = getAdvertProps(creative);

  return {
    ...advertProps,
    creative: isRadarCreative(creative, fromRadar)
      ? getRadarCreative(creative)
      : getConductorCreative(creative),
  };
};
